import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Download, ExternalLink, Lock } from 'lucide-react';
import { slugify } from '../utils/stringUtils.js';

interface QrCodeViewerProps {
  uibn: string;
  bookTitle?: string;
  size?: number;
  className?: string;
  showDownload?: boolean;
  directUrl?: string;
  pure?: boolean;
}

/**
 * Draw custom rounded rectangle with high precision
 */
const drawRoundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
};

/**
 * Render UIBN badge on any QR canvas given its resolution
 */
const drawUibnBadgeOnCanvas = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const cx = canvas.width / 2;
  const cy = canvas.height / 2;

  // Proportional badge dimensions
  const badgeWidth = canvas.width * 0.32;
  const badgeHeight = canvas.height * 0.18;
  const radius = Math.max(4, Math.floor(canvas.width * 0.025));
  const padding = Math.max(2, Math.floor(canvas.width * 0.015));

  // 1. Draw white background buffer to isolate patterns
  ctx.fillStyle = '#ffffff';
  drawRoundRect(
    ctx,
    cx - badgeWidth / 2 - padding,
    cy - badgeHeight / 2 - padding,
    badgeWidth + padding * 2,
    badgeHeight + padding * 2,
    radius + 2
  );

  // 2. Draw vibrant indigo badge in the center
  ctx.fillStyle = '#4f46e5';
  drawRoundRect(
    ctx,
    cx - badgeWidth / 2,
    cy - badgeHeight / 2,
    badgeWidth,
    badgeHeight,
    radius
  );

  // 3. Draw crisp white UIBN branding text
  ctx.fillStyle = '#ffffff';
  const fontSize = Math.floor(badgeHeight * 0.62);
  ctx.font = `800 ${fontSize}px "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('UIBN', cx, cy + Math.floor(fontSize * 0.04));
};

export const QrCodeViewer: React.FC<QrCodeViewerProps> = ({
  uibn,
  bookTitle,
  size = 140,
  className = '',
  showDownload = true,
  directUrl,
  pure = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const slug = bookTitle ? slugify(bookTitle) : uibn;
  const canonicalUrl = directUrl || `${window.location.origin}/buku/${encodeURIComponent(uibn)}`;

  // Screen display preview (2x supersampling for high-DPI/Retina screens)
  useEffect(() => {
    if (!canvasRef.current || !uibn) return;

    const renderSize = pure ? size * 2 : Math.max(280, size * 2);

    QRCode.toCanvas(
      canvasRef.current,
      canonicalUrl,
      {
        width: renderSize,
        margin: pure ? 0 : 2,
        color: {
          dark: '#1e1b4b', // Premium deep navy
          light: '#ffffff',
        },
        errorCorrectionLevel: pure ? 'M' : 'H', // Medium level is perfect and clean for pure scan
      },
      (err) => {
        if (err) {
          console.error('QR code render error:', err);
          return;
        }
        if (canvasRef.current && !pure) {
          drawUibnBadgeOnCanvas(canvasRef.current);
        }
      }
    );
  }, [uibn, bookTitle, canonicalUrl, size, pure]);

  // Master High-Definition PNG Download (1200 x 1200 px, 300 DPI Print Quality)
  const downloadPng = () => {
    try {
      const exportCanvas = document.createElement('canvas');
      const EXPORT_RESOLUTION = 1200; // Ultra HD resolution

      QRCode.toCanvas(
        exportCanvas,
        canonicalUrl,
        {
          width: EXPORT_RESOLUTION,
          margin: 2,
          color: {
            dark: '#0f172a', // Deep crisp slate
            light: '#ffffff',
          },
          errorCorrectionLevel: 'H',
        },
        (err) => {
          if (err) {
            console.error('Failed to render high-res QR code:', err);
            return;
          }

          drawUibnBadgeOnCanvas(exportCanvas);

          const link = document.createElement('a');
          link.href = exportCanvas.toDataURL('image/png');
          link.download = `qrcode-hd-300dpi-${slug || uibn}.png`;
          link.click();
        }
      );
    } catch (err) {
      console.error('Error downloading high-res QR PNG:', err);
    }
  };

  const downloadSvg = async () => {
    try {
      const svgString = await QRCode.toString(canonicalUrl, {
        type: 'svg',
        margin: 1,
        color: {
          dark: '#1e1b4b',
          light: '#ffffff',
        },
        errorCorrectionLevel: 'H',
      });
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qrcode-vektor-${slug || uibn}.svg`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to generate SVG QR code:', err);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(canonicalUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  if (pure) {
    return (
      <canvas
        ref={canvasRef}
        className="rounded-md block shadow-xs"
        style={{ width: `${size}px`, height: `${size}px` }}
      />
    );
  }

  return (
    <div id={`qr-container-${uibn}`} className={`flex flex-col items-center bg-white p-3 rounded-xl border border-slate-200 shadow-xs ${className}`}>
      <div className={`flex justify-center bg-white p-1 rounded-lg ${!showDownload ? 'select-none pointer-events-none' : ''}`}>
        <canvas
          ref={canvasRef}
          className="rounded"
          style={{ width: `${size}px`, height: `${size}px` }}
          onContextMenu={!showDownload ? (e) => e.preventDefault() : undefined}
        />
      </div>
      
      {/* Target URL indicator & Dimension */}
      <div className="mt-1.5 flex items-center justify-between w-full px-1 text-[10px] text-slate-500">
        <span className="font-mono text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100 truncate max-w-[150px]">
          /buku/{slug}
        </span>
        <span className="font-mono text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
          HD 300 DPI
        </span>
      </div>

      {showDownload ? (
        <div className="mt-2 flex items-center justify-between w-full pt-2 border-t border-slate-100 text-xs gap-2">
          <button
            type="button"
            onClick={handleCopyLink}
            className="flex-1 py-1 px-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition cursor-pointer text-center text-[11px] truncate flex items-center justify-center gap-1"
            title="Salin tautan buku"
          >
            <ExternalLink className="w-3 h-3 text-indigo-600" />
            <span>{copiedUrl ? 'Tersalin!' : 'Salin URL'}</span>
          </button>
          <div className="flex gap-1">
            <button
              id={`btn-download-qr-png-${uibn}`}
              type="button"
              onClick={downloadPng}
              className="p-1 px-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition flex items-center gap-1 cursor-pointer text-[11px] shadow-2xs"
              title="Unduh PNG Resolusi Tinggi (1200x1200px / 300 DPI Siap Cetak)"
            >
              <Download className="w-3 h-3" />
              <span>PNG HD</span>
            </button>
            <button
              id={`btn-download-qr-svg-${uibn}`}
              type="button"
              onClick={downloadSvg}
              className="p-1 px-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-700 font-medium transition flex items-center gap-1 cursor-pointer text-[11px]"
              title="Unduh format Vektor SVG"
            >
              <span>SVG</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 w-full pt-1.5 border-t border-slate-100 text-center">
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 font-medium">
            <Lock className="w-3 h-3 text-slate-400" />
            <span>Pratinjau Publik (Unduh/Salin Terproteksi)</span>
          </span>
        </div>
      )}
    </div>
  );
};
